import React, { useState } from 'react';
import { Info } from 'lucide-react';
import { formatGrade } from '@utils/gradeUtils.js';
import { CLIMBING_STYLES } from '@utils/constants.js';
import GradeModal from './GradeModal.js';

const CustomTooltip = ({ text, children }) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {isVisible && (
        <div className="absolute z-50 w-64 p-2 text-sm text-white bg-gray-800 rounded shadow-lg -translate-y-full -top-2 left-full ml-2">
          {text}
        </div>
      )}
    </div>
  );
};

const MetricsCard = ({ label, value, subValue, subLabel, tooltip }) => (
  <div className="bg-white p-2 rounded shadow-sm border border-gray-200">
    <div className="flex items-center justify-between mb-1">
      <div className="text-xs text-gray-600">
        {tooltip ? (
          <CustomTooltip text={tooltip}>
            <div className="flex items-center gap-1">
              {label}
              <Info className="h-3 w-3 text-gray-400" />
            </div>
          </CustomTooltip>
        ) : (
          label
        )}
      </div>
    </div>
    <div className="text-lg font-semibold leading-tight">
      {value ?? 'N/A'}
    </div>
    {(subValue || subLabel) && (
      <div className="mt-1 pt-1 border-t border-gray-100">
        <div className="flex justify-between items-center text-xs">
          <span className="text-gray-500">{subValue}</span>
          <span className="font-medium">{subLabel ?? 'N/A'}</span>
        </div>
      </div>
    )}
  </div>
);

// Helper function for integer-like numbers
const isEffectivelyInteger = (num) => Math.abs(Math.round(num) - num) < 1e-6;

// Helper function for Ewbank grades
const formatEwbankGrade = (grade) => {
  if (isEffectivelyInteger(grade)) {
    return Math.round(grade).toString();
  }
  return grade.toFixed(2);
};

// Helper function to safely format grades with proper handling
const safeGradeFormat = (grade, gradeSystem, climbingStyle) => {
  if (grade === undefined || grade === null) return 'N/A';
  if (typeof grade === 'string') return grade;
  if (gradeSystem === 'ewbank' && climbingStyle !== CLIMBING_STYLES.BOULDER) {
    return formatEwbankGrade(grade);
  }
  return formatGrade(grade, gradeSystem, climbingStyle);
};

// Helper function to format CPG/CGE values
const formatCumulativeGrade = (value, gradeSystem, climbingStyle) => {
  if (value === undefined || value === null) return 'N/A';
  if (typeof value !== 'number') return value;
  if (climbingStyle === CLIMBING_STYLES.BOULDER) {
    return `V${value.toFixed(1)}`;
  }
  if (gradeSystem === 'ewbank') {
    return value.toFixed(2);
  }
  return formatGrade(value, gradeSystem, climbingStyle);
};

export const MetricsGrid = ({ metrics, gradeSystem, climbingStyle }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);

  if (!metrics) return null;

  const handleDogListClick = () => {
    if (metrics.dogListRoutes?.length) {
      setModalData({
        grade: 'Dog List',
        routes: metrics.dogListRoutes.sort((a, b) => 
          new Date(b["Ascent Date"]) - new Date(a["Ascent Date"])
        )
      });
      setIsModalOpen(true);
    }
  };

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        <MetricsCard 
          label="Total Ascents"
          value={metrics.totalAscents ?? 0}
          subValue={`${metrics.cleanAscents ?? 0} clean`}
          subLabel={`${metrics.failedAscents ?? 0} attempts`}
          tooltip="Total number of climbing attempts, including both successful and unsuccessful attempts"
        />
        <MetricsCard 
          label="Unique Routes"
          value={metrics.distinctRoutes ?? 0}
          subValue={`${metrics.cleanRoutes ?? 0} clean`}
          subLabel={
            <span 
              className="cursor-pointer hover:text-blue-600"
              onClick={handleDogListClick}
            >
              {`${(metrics.distinctRoutes ?? 0) - (metrics.cleanRoutes ?? 0)} dog-list`}
            </span>
          }
          tooltip="Number of different routes attempted, with breakdown of clean sends vs projects still in progress"
        />
        <MetricsCard 
          label="Highest Clean"
          value={safeGradeFormat(metrics.highestCleanGrade, gradeSystem, climbingStyle)}
          subValue="Avg clean:"
          subLabel={safeGradeFormat(metrics.averageCleanGrade, gradeSystem, climbingStyle)}
          tooltip="Highest grade climbed clean (no falls/takes) and average grade of clean ascents"
        />
        <MetricsCard 
          label="First Go Grade"
          value={safeGradeFormat(metrics.firstGoGrade, gradeSystem, climbingStyle)}
          subValue="Flash slope:"
          subLabel={metrics.flashSlope?.toFixed(2) ?? 'N/A'}
          tooltip="Estimated grade you can consistently flash/onsight, with flash probability trend"
        />
        <MetricsCard 
          label="CGP (Performance)"
          value={formatCumulativeGrade(metrics.cgp, gradeSystem, climbingStyle)}
          subValue="Per day:"
          subLabel={formatCumulativeGrade(metrics.cgpPerDay, gradeSystem, climbingStyle)}
          tooltip="Cumulative Grade Performance: Overall score based on successful clean ascents only"
        />
        <MetricsCard 
          label="CGE (Effort)"
          value={formatCumulativeGrade(metrics.cge, gradeSystem, climbingStyle)}
          subValue="Per day:"
          subLabel={formatCumulativeGrade(metrics.cgePerDay, gradeSystem, climbingStyle)}
          tooltip="Cumulative Grade Effort: Overall score based on all attempts including failures and hangdogs"
        />
      </div>

      <GradeModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        gradeData={modalData}
        gradeSystem={gradeSystem}
        climbingStyle={climbingStyle}
      />
    </>
  );
};